export interface WatchCameraInput {
  longitude: number
  latitude: number
  vehicleBearing: number
  orbitYaw: number
  pitch: number
  zoom: number
  viewportWidth: number
  viewportHeight: number
}

export interface WatchCameraPose {
  center: [number, number]
  bearing: number
  pitch: number
  zoom: number
  offset: [number, number]
}

export interface WatchCameraFollowPose {
  longitude: number
  latitude: number
  vehicleBearing: number
}

// The model heading intentionally reacts fairly quickly so the cars line up with the
// track. A near-horizon camera must be more inertial: copying those small track-vertex
// corrections directly rotates the entire city around the train. Position remains locked
// to the rendered lead car; even a small geographic chase lag becomes many screen pixels
// at close zoom. The heading time constant is independent of display refresh rate.
const WATCH_BEARING_TAU_SECONDS = 0.65
const WATCH_MAX_FRAME_SECONDS = 0.1

// MapLibre's web-mercator scale, as its 512 px world tile. Framing needs this both ways
// — the zoom that yields a given ground resolution, and the resolution at a given zoom —
// so the two constants are named once here and the pair below is derived from them
// rather than the expression being written out twice.
//
// Deliberately NOT transitMapGeometry's metresPerPixel, and not merged with it: this
// module is a dependency-free camera model (see its test, which needs no mocks), and the
// framing maths clamps cosLat so a near-polar latitude can't blow the zoom up. That
// clamp is the difference, not an oversight.
const EQUATOR_METRES = 40_075_016.686
const WORLD_TILE_PX = 512
const metresPerPixelAt = (zoom: number, cosLat: number) =>
  (EQUATOR_METRES * cosLat) / (WORLD_TILE_PX * 2 ** zoom)
const zoomForMetresPerPixel = (metresPerPixel: number, cosLat: number) =>
  Math.log2((EQUATOR_METRES * cosLat) / (WORLD_TILE_PX * metresPerPixel))

/**
 * Low-pass the vehicle heading used by the chase camera while keeping its geographic
 * target exactly on the rendered lead car. Passing null seeds without an entrance
 * animation.
 */
export function followWatchCameraPose(
  previous: WatchCameraFollowPose | null,
  target: WatchCameraFollowPose,
  elapsedSeconds: number,
): WatchCameraFollowPose {
  if (!previous) return { ...target }

  const dt = Math.max(0, Math.min(WATCH_MAX_FRAME_SECONDS, elapsedSeconds))
  if (dt === 0) return previous

  const bearingMix = 1 - Math.exp(-dt / WATCH_BEARING_TAU_SECONDS)
  const bearingDelta = ((target.vehicleBearing - previous.vehicleBearing + 540) % 360) - 180

  return {
    longitude: target.longitude,
    latitude: target.latitude,
    vehicleBearing: ((previous.vehicleBearing + bearingDelta * bearingMix) % 360 + 360) % 360,
  }
}

// Keep the front car itself as the geographic camera target, then place that target
// at a fixed screen-space position below center. Screen-space anchoring is invariant
// under zoom and orbit changes; the old fixed-metre look-ahead could push the car out
// of frame when zoomed in.
export function watchCameraPose(input: WatchCameraInput): WatchCameraPose {
  const bearing = ((input.vehicleBearing + input.orbitYaw) % 360 + 360) % 360
  // Reserve 24 m for an ~18.4 m R160 car plus visual margin. Use the shorter
  // viewport dimension because orbiting can rotate the car onto either screen axis.
  const cosLat = Math.max(0.2, Math.cos(input.latitude * Math.PI / 180))
  const shortSide = Math.max(1, Math.min(input.viewportWidth, input.viewportHeight))
  const usablePixels = Math.max(1, shortSide * 0.72)
  const requiredMetersPerPixel = 24 / usablePixels
  const maxFramingZoom = zoomForMetresPerPixel(requiredMetersPerPixel, cosLat)
  const zoom = Math.min(input.zoom, maxFramingZoom)
  const framingHalfPixels = 12 / metresPerPixelAt(zoom, cosLat)
  const desiredLowerOffset = Math.min(140, input.viewportHeight * 0.20)
  const maxLowerOffset = Math.max(0, input.viewportHeight / 2 - framingHalfPixels - 16)

  return {
    center: [input.longitude, input.latitude],
    bearing,
    pitch: input.pitch,
    zoom,
    // MapLibre positions `center` at viewport-center + offset. Capping the offset
    // keeps the lead car comfortably inside short landscape viewports as well.
    offset: [0, Math.min(desiredLowerOffset, maxLowerOffset)],
  }
}
